import { useMyLeagueStanding, useLeagueLeaderboard, TIER_META } from "@/hooks/useLeagues";
import { Loader2, TrendingUp, TrendingDown, Trophy, Crown } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";

export function LeaguePanel() {
  const { standing, loading } = useMyLeagueStanding();
  const { rows, loading: lbLoading } = useLeagueLeaderboard();

  if (loading || lbLoading) {
    return (
      <div className="glass-card p-6 flex justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-primary" />
      </div>
    );
  }
  if (!standing) return null;

  const tier = TIER_META[standing.tier];

  return (
    <div className="glass-card p-5 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className={cn("w-12 h-12 rounded-xl bg-gradient-to-br flex items-center justify-center text-xl shadow-lg", tier.gradient)}>
            {tier.emoji}
          </div>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Season {standing.season_number} League</p>
            <h3 className={cn("font-heading font-bold text-lg truncate", tier.color)}>{tier.label} Division</h3>
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="text-xs text-muted-foreground">Resets</p>
          <p className="text-xs font-semibold text-foreground">{formatDistanceToNow(new Date(standing.ends_at))}</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="p-2 rounded-lg bg-muted/30">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">XP Week</p>
          <p className="font-heading font-bold text-foreground">{standing.weekly_xp}</p>
        </div>
        <div className="p-2 rounded-lg bg-muted/30">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Rank</p>
          <p className="font-heading font-bold text-foreground">{standing.my_rank ?? "—"}/{standing.total_in_tier}</p>
        </div>
        <div className="p-2 rounded-lg bg-muted/30">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Promote</p>
          <p className="font-heading font-bold text-primary">Top {standing.promotion_threshold}</p>
        </div>
      </div>

      <div className="space-y-1.5 max-h-72 overflow-y-auto">
        {rows.map(r => (
          <div key={r.user_id} className={cn(
            "flex items-center gap-3 p-2 rounded-lg",
            r.is_self ? "bg-primary/10 border border-primary/30" : "bg-muted/20"
          )}>
            <span className="w-6 text-xs font-bold text-muted-foreground text-center">{r.rank}</span>
            <div className="w-7 h-7 rounded-md bg-card flex items-center justify-center text-[10px] font-bold text-foreground">
              {(r.display_name || "?").slice(0, 2).toUpperCase()}
            </div>
            <p className="flex-1 text-sm font-medium text-foreground truncate">{r.display_name || "Anonymous"}{r.is_self && <span className="text-primary text-xs ml-1">(you)</span>}</p>
            {r.in_promotion_zone && <TrendingUp className="w-3.5 h-3.5 text-primary" />}
            {r.in_demotion_zone && <TrendingDown className="w-3.5 h-3.5 text-destructive" />}
            <span className="text-sm font-heading font-bold text-foreground tabular-nums">{r.weekly_xp}</span>
          </div>
        ))}
        {rows.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-4">No standings yet. Earn XP this week to enter the league.</p>
        )}
      </div>
    </div>
  );
}
